import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, UserPlus, Search, Sparkles, Building2, RefreshCw, Shield, ClipboardCheck, Mail, Download, Pencil, Handshake } from "lucide-react";
import { api, formatApiErrorDetail } from "../../api";
import { useAuth } from "../../auth";
import { PageHeader, Btn, Badge, dt, eur } from "../../components/common";
import { Modal, Field, TextArea, Selecter, masks } from "./crmui";
import AccountDrawer from "../../components/AccountDrawer";
import PlatformFilter from "../../components/PlatformFilter";
import { useAutoRefresh } from "../../lib/autoRefresh";
import { HighlightText } from "../../lib/highlight";

const TIPOS = [
  { value: "lead", label: "Lead" },
  { value: "cliente", label: "Cliente" },
  { value: "parceiro", label: "Parceiro" },
  { value: "fornecedor", label: "Fornecedor" },
];
const ORIGENS = [
  { value: "email", label: "Email" },
  { value: "chamada", label: "Chamada" },
  { value: "formulario", label: "Formulario" },
  { value: "agendamento", label: "Agendamento" },
  { value: "demo", label: "Demo" },
  { value: "chat", label: "Chat" },
  { value: "parceiro", label: "Parceiro" },
  { value: "manual", label: "Manual" },
];
const RGPD_BASE_LEGAL = [
  { value: "", label: "- Nao aplicavel -" },
  { value: "consentimento", label: "Consentimento (art. 6.1.a)" },
  { value: "contrato", label: "Execucao de contrato (art. 6.1.b)" },
  { value: "obrigacao_legal", label: "Obrigacao legal (art. 6.1.c)" },
  { value: "interesse_vital", label: "Interesse vital (art. 6.1.d)" },
  { value: "interesse_publico", label: "Interesse publico (art. 6.1.e)" },
  { value: "interesse_legitimo", label: "Interesse legitimo (art. 6.1.f)" },
];
const CANAL_CAPTURA = ["site", "telefone", "email", "evento", "referral", "outbound", "parceiro", "outros"];
const DIMENSOES = [
  { value: "", label: "- Nao definida -" },
  { value: "small", label: "Pequena (<50)" },
  { value: "medium", label: "Media (50-249)" },
  { value: "large", label: "Grande (250-999)" },
  { value: "enterprise", label: "Enterprise (>=1000)" },
];

const EMPTY_ACC = {
  nome: "", nif: "", tipo: "lead", origem: "manual",
  morada: "", codigo_postal: "", localidade: "", cidade: "", distrito: "", pais: "PT",
  website: "", email: "", telefone: "",
  setor: "", area_negocio: "", dimensao: "", numero_colaboradores: "", cae: "",
  fatura_a_nome_de: "", morada_faturacao: "", codigo_postal_faturacao: "", localidade_faturacao: "", cidade_faturacao: "",
  distrito_faturacao: "", pais_faturacao: "PT", iban: "", contacto_faturacao_email: "", condicoes_pagamento: "30 dias",
  metodo_pagamento_pref: "",
  rgpd_base_legal: "", rgpd_finalidade: "", rgpd_consentimento_em: "", rgpd_canal_captura: "",
  rgpd_retencao_meses: "", rgpd_direito_esquecimento: false,
  parceiro_id: "", parceiro_nome: "",
  notas: "",
};

function SectionTitle({ children }) {
  return <div className="text-[11px] font-bold uppercase tracking-wider text-navy border-b border-tint pb-1 mt-2 mb-2">{children}</div>;
}

export default function Accounts() {
  const nav = useNavigate();
  const { can } = useAuth();
  const isSuper = can("*");
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [platformOrgNames, setPlatformOrgNames] = useState(null); // null = todas, Set = filtrar
  const [plataforma, setPlataforma] = useState(() => localStorage.getItem("sh_search_platform") || "");
  const [dpoFilter, setDpoFilter] = useState("");
  const [triageFilter, setTriageFilter] = useState("");
  const [q, setQ] = useState("");
  const [localidadeFilter, setLocalidadeFilter] = useState(() => searchParams.get("localidade") || "");
  const [cpFilter, setCpFilter] = useState("");
  const [marketingFilter, setMarketingFilter] = useState(false);
  const [marketingAccountIds, setMarketingAccountIds] = useState(null); // null = todas
  const [arquivadasFilter, setArquivadasFilter] = useState(false);
  const [sortByScore, setSortByScore] = useState(false);
  const [sortMode, setSortMode] = useState("alpha"); // alpha | recent (ignorado se sortByScore activo)
  const [minScore, setMinScore] = useState(0);
  const [oportunidadeFilter, setOportunidadeFilter] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showLead, setShowLead] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [acc, setAcc] = useState(EMPTY_ACC);
  const [nextCode, setNextCode] = useState("");
  const [busy, setBusy] = useState({ vies: false, ai: false, cae: false, save: false, cae_bulk: false });
  const [lead, setLead] = useState({ nome_contacto: "", email: "", telefone: "", organizacao: "", nif: "", origem: "formulario", plataforma: "", mensagem: "" });
  const [platforms, setPlatforms] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    api.get("/platforms").then(({ data }) => setPlatforms(data || [])).catch(() => setPlatforms([]));
    api.get("/partners").then(({ data }) => setPartners(data || [])).catch(() => setPartners([]));
  }, []);

  const load = async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (dpoFilter) params.dpo_mode = dpoFilter;
      if (triageFilter) params.triage_mode = triageFilter;
      if (localidadeFilter) params.localidade = localidadeFilter;
      if (cpFilter) params.codigo_postal = cpFilter;
      if (arquivadasFilter) params.arquivadas = "1";
      const { data } = await api.get("/crm/accounts", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, [dpoFilter, triageFilter, localidadeFilter, cpFilter, arquivadasFilter]);
  useAutoRefresh(load); // eslint-disable-line

  // Abrir modal de edicao a partir de ?edit_id={id}
  useEffect(() => {
    const eid = searchParams.get("edit_id");
    if (eid) {
      openEditAccount({ stopPropagation: () => {} }, eid);
      // Limpa o param
      const p = new URLSearchParams(searchParams);
      p.delete("edit_id");
      window.history.replaceState(null, "", "/app/crm/contas" + (p.toString() ? `?${p}` : ""));
    }
  }, []); // eslint-disable-line

  // Filtro marketing: quando activo, carrega contactos com opt-in e reduz as contas
  useEffect(() => {
    if (!marketingFilter) { setMarketingAccountIds(null); return; }
    api.get("/crm/contacts", { params: { marketing: "optin" } })
      .then(({ data }) => {
        const ids = new Set((data || []).map((c) => c.account_id).filter(Boolean));
        setMarketingAccountIds(ids);
      })
      .catch(() => setMarketingAccountIds(new Set()));
  }, [marketingFilter]);

  const exportContactsCsv = async () => {
    try {
      const { data } = await api.get("/crm/contacts/export.csv", {
        params: { marketing: "optin" }, responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "contactos-marketing-optin.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // Carrega organizacao_names das subscricoes da plataforma escolhida (para filtrar contas)
  useEffect(() => {
    if (!plataforma) { setPlatformOrgNames(null); return; }
    api.get(`/subscriptions`, { params: {} })
      .then(({ data }) => {
        const p = plataforma.toLowerCase();
        const names = new Set((data || [])
          .filter((s) => (s.plataforma || "").toLowerCase() === p)
          .map((s) => (s.organizacao_nome || "").toLowerCase())
          .filter(Boolean));
        setPlatformOrgNames(names);
      })
      .catch(() => setPlatformOrgNames(new Set()));
  }, [plataforma]);

  const displayRows = (() => {
    let list = (platformOrgNames
      ? rows.filter((r) => platformOrgNames.has((r.nome || "").toLowerCase()))
      : rows)
      .filter((r) => !marketingAccountIds || marketingAccountIds.has(r.id));
    if (minScore > 0) list = list.filter((r) => (r.lead_score || 0) >= minScore);
    if (oportunidadeFilter) list = list.filter((r) => r.ciclo_vida === "opportunity" || r.n_oportunidades > 0);
    if (sortByScore) list = [...list].sort((a, b) => (b.lead_score || 0) - (a.lead_score || 0));
    else if (sortMode === "recent") list = [...list].sort((a, b) => new Date(b.criado_em || 0) - new Date(a.criado_em || 0));
    return list;
  })();
  const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const prevCount = useRef(displayRows.length);
  const totalPages = Math.max(1, Math.ceil(displayRows.length / pageSize));
  useEffect(() => {
    if (prevCount.current !== displayRows.length) { prevCount.current = displayRows.length; setPage(1); }
    else if (page > totalPages) setPage(1);
  }, [displayRows.length, totalPages, page]);
  const pagedRows = displayRows.slice((page - 1) * pageSize, page * pageSize);
  const pageFrom = displayRows.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageTo = Math.min(page * pageSize, displayRows.length);

  const openNewAccount = async () => {
    setEditingId(null);
    setAcc(EMPTY_ACC);
    setShowAccount(true);
    try {
      const { data } = await api.get(`/crm/account-code/next?tipo=lead`);
      setNextCode(data.codigo);
    } catch { setNextCode(""); }
  };

  const openEditAccount = async (e, id) => {
    e.stopPropagation();
    try {
      const { data } = await api.get(`/crm/accounts/${id}`);
      // Preenche o formulario com os valores existentes (mantendo as chaves esperadas)
      const filled = { ...EMPTY_ACC };
      Object.keys(EMPTY_ACC).forEach((k) => { if (data[k] !== undefined && data[k] !== null) filled[k] = data[k]; });
      setAcc(filled);
      setEditingId(id);
      setNextCode(data.codigo || "");
      setShowAccount(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const setField = (patch) => setAcc((a) => ({ ...a, ...patch }));

  const refreshCode = async (tipo) => {
    try {
      const { data } = await api.get(`/crm/account-code/next?tipo=${tipo}`);
      setNextCode(data.codigo);
    } catch { /* silencio */ }
  };

  const doCPLookup = async (cp, target = "principal") => {
    if (!cp || !/^\d{4}-\d{3}$/.test(cp)) return;
    try {
      const { data } = await api.get(`/crm/cp-lookup/${cp}`);
      if (data.found && data.localidade) {
        if (target === "faturacao") {
          setField({
            localidade_faturacao: acc.localidade_faturacao || data.localidade,
            cidade_faturacao: acc.cidade_faturacao || data.cidade || data.concelho,
            distrito_faturacao: acc.distrito_faturacao || data.distrito,
          });
        } else {
          setField({
            localidade: acc.localidade || data.localidade,
            cidade: acc.cidade || data.cidade || data.concelho,
            distrito: acc.distrito || data.distrito,
          });
        }
        toast.success(`CP: ${data.localidade}${data.distrito ? ` (${data.distrito})` : ""}`);
      }
    } catch { /* silent - fallback ao input manual */ }
  };

  const doVies = async () => {
    if (!acc.nif.trim()) return toast.error("Indique o NIF.");
    setBusy((b) => ({ ...b, vies: true }));
    try {
      const { data } = await api.get(`/crm/vies/lookup`, { params: { nif: acc.nif, country: acc.pais || "PT" } });
      if (!data.valid) {
        toast.warning("NIF nao validado no VIES.");
        return;
      }
      setField({
        nome: acc.nome || data.name,
        morada: acc.morada || data.morada || data.address,
        codigo_postal: acc.codigo_postal || data.codigo_postal,
        localidade: acc.localidade || data.localidade,
        cidade: acc.cidade || data.cidade,
        distrito: acc.distrito || data.distrito,
        fatura_a_nome_de: acc.fatura_a_nome_de || data.name,
        morada_faturacao: acc.morada_faturacao || data.morada || data.address,
        codigo_postal_faturacao: acc.codigo_postal_faturacao || data.codigo_postal,
        localidade_faturacao: acc.localidade_faturacao || data.localidade,
        cidade_faturacao: acc.cidade_faturacao || data.cidade,
        distrito_faturacao: acc.distrito_faturacao || data.distrito,
      });
      // Fallback: se VIES nao trouxe distrito mas ha CP, tenta geoapi.pt
      if (!data.distrito && data.codigo_postal) {
        doCPLookup(data.codigo_postal, "principal");
        doCPLookup(data.codigo_postal, "faturacao");
      }
      toast.success(`VIES: ${data.name}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "VIES indisponivel.");
    } finally { setBusy((b) => ({ ...b, vies: false })); }
  };

  const doAI = async () => {
    if (!acc.nome.trim() && !acc.nif.trim()) return toast.error("Indique pelo menos nome ou NIF.");
    setBusy((b) => ({ ...b, ai: true }));
    try {
      const { data } = await api.post(`/crm/enrich/ai`, {
        nome: acc.nome, nif: acc.nif, pais: acc.pais, website: acc.website, morada: acc.morada, setor_atual: acc.setor,
      });
      setField({
        setor: acc.setor || data.setor || "",
        area_negocio: acc.area_negocio || data.area_negocio || "",
        dimensao: acc.dimensao || data.dimensao || "",
        cae: acc.cae || data.cae || "",
        website: acc.website || (data.website_sugerido ? (data.website_sugerido.startsWith("http") ? data.website_sugerido : `https://${data.website_sugerido}`) : ""),
        notas: acc.notas ? acc.notas : (data.descricao || ""),
      });
      toast.success("Enriquecido com IA.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro no enriquecimento IA.");
    } finally { setBusy((b) => ({ ...b, ai: false })); }
  };

  const doCAE = async () => {
    if (!acc.nif.trim() && !acc.nome.trim()) return toast.error("Indique o NIF ou nome para pesquisar o CAE.");
    setBusy((b) => ({ ...b, cae: true }));
    try {
      const { data } = await api.post(`/crm/nif/cae-lookup`, { nif: acc.nif, nome: acc.nome, setor_hint: acc.setor });
      if (!data.cae && !data.setor) { toast.warning("Nao foi possivel determinar o CAE."); return; }
      setField({
        cae: data.cae || acc.cae,
        setor: acc.setor || data.setor || "",
        area_negocio: acc.area_negocio || data.area_negocio || "",
      });
      toast.success(`CAE ${data.cae} - ${data.setor}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao pesquisar CAE.");
    } finally { setBusy((b) => ({ ...b, cae: false })); }
  };

  const fillCaeBulk = async () => {
    setBusy((b) => ({ ...b, cae_bulk: true }));
    try {
      const { data } = await api.post("/crm/accounts/fill-cae-bulk");
      toast.success(data.atualizadas > 0
        ? `CAE preenchido em ${data.atualizadas} conta(s) neste lote. Restam ${data.total_sem_cae - data.atualizadas} sem CAE.`
        : "Sem contas a processar ou nenhum CAE determinado.");
      if (data.atualizadas > 0) load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao preencher CAE em massa.");
    } finally { setBusy((b) => ({ ...b, cae_bulk: false })); }
  };

  const createAccount = async () => {
    if (!acc.nome.trim()) return toast.error("Indique o nome da conta.");
    setBusy((b) => ({ ...b, save: true }));
    try {
      const payload = { ...acc };
      if (payload.numero_colaboradores === "") payload.numero_colaboradores = null;
      else payload.numero_colaboradores = parseInt(payload.numero_colaboradores) || null;
      if (payload.rgpd_retencao_meses === "") payload.rgpd_retencao_meses = null;
      else payload.rgpd_retencao_meses = parseInt(payload.rgpd_retencao_meses) || null;
      if (editingId) {
        await api.put(`/crm/accounts/${editingId}`, payload);
        toast.success("Conta atualizada.");
      } else {
        await api.post("/crm/accounts", payload);
        toast.success("Conta criada.");
      }
      setShowAccount(false);
      setEditingId(null);
      setAcc(EMPTY_ACC);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy((b) => ({ ...b, save: false })); }
  };

  const createLead = async () => {
    if (!lead.nome_contacto.trim()) return toast.error("Indique o nome do contacto.");
    try {
      await api.post("/crm/leads", lead);
      toast.success("Lead criado com conta e contacto.");
      setShowLead(false);
      setLead({ nome_contacto: "", email: "", telefone: "", organizacao: "", nif: "", origem: "formulario", plataforma: "", mensagem: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <>
      <PageHeader
        overline="CRM"
        title="Contas"
        action={
          <div className="flex gap-2 flex-wrap">
            <Btn variant="ghost" onClick={fillCaeBulk} disabled={busy.cae_bulk} testid="fill-cae-bulk-btn">
              <Sparkles size={14} /> {busy.cae_bulk ? "A processar..." : "Preencher CAE em falta"}
            </Btn>
            <Btn variant="ghost" onClick={() => setShowLead(true)} testid="new-lead-btn"><UserPlus size={16} /> Novo lead</Btn>
            <Btn onClick={openNewAccount} testid="new-account-btn"><Plus size={16} /> Nova conta</Btn>
          </div>
        }
      />

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Pesquisar por nome, NIF ou codigo"
            data-testid="account-search"
            className="w-full pl-9 pr-3 py-2 border border-tint rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue"
          />
        </div>
        <Btn variant="ghost" onClick={load} testid="account-search-btn">Pesquisar</Btn>
        <div className="flex items-center gap-2">
          <input
            value={localidadeFilter}
            onChange={(e) => setLocalidadeFilter(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Localidade..."
            data-testid="account-filter-localidade"
            className="w-40 px-3 py-2 border border-tint rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue"
          />
          <input
            value={cpFilter}
            onChange={(e) => setCpFilter(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="CP (ex: 4760)"
            data-testid="account-filter-cp"
            className="w-32 px-3 py-2 border border-tint rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue"
          />
          {(localidadeFilter || cpFilter) && (
            <button onClick={() => { setLocalidadeFilter(""); setCpFilter(""); }}
              data-testid="account-filter-clear"
              className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-navy hover:text-white transition-colors">
              Limpar
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-muted2">Plataforma:</span>
          <PlatformFilter value={plataforma} onChange={setPlataforma} testid="accounts-platform-filter" />
          {plataforma && (
            <span className="text-xs text-muted2" data-testid="accounts-platform-count">
              {displayRows.length} de {rows.length}
            </span>
          )}
        </div>
      </div>

      {/* Filtros DPO e Triagem (servico interno vs cliente) */}
      <div className="flex items-center gap-4 mb-4 flex-wrap text-xs" data-testid="accounts-service-filters">
        <div className="flex items-center gap-1.5">
          <Shield size={13} className="text-blue" />
          <span className="text-muted2 font-semibold uppercase tracking-wider">DPO:</span>
          {[
            { v: "", l: "Todos" },
            { v: "cogniseg", l: "Interno (Cogniseg)", tone: "bg-blue text-white border-blue" },
            { v: "client", l: "Cliente", tone: "bg-amber-500 text-white border-amber-500" },
            { v: "sem_info", l: "Sem info", tone: "bg-slate-400 text-white border-slate-400" },
          ].map((opt) => (
            <button
              key={`dpo-${opt.v}`}
              type="button"
              onClick={() => setDpoFilter(opt.v)}
              data-testid={`filter-dpo-${opt.v || "todos"}`}
              className={`px-2 py-1 rounded-full border text-[11px] font-semibold transition-colors ${dpoFilter === opt.v ? (opt.tone || "bg-navy text-white border-navy") : "bg-white text-navy border-tint hover:border-blue"}`}
            >
              {opt.l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <ClipboardCheck size={13} className="text-blue" />
          <span className="text-muted2 font-semibold uppercase tracking-wider">Triagem:</span>
          {[
            { v: "", l: "Todos" },
            { v: "cogniseg", l: "Interno (Cogniseg)", tone: "bg-blue text-white border-blue" },
            { v: "client", l: "Cliente", tone: "bg-amber-500 text-white border-amber-500" },
            { v: "sem_info", l: "Sem info", tone: "bg-slate-400 text-white border-slate-400" },
          ].map((opt) => (
            <button
              key={`tri-${opt.v}`}
              type="button"
              onClick={() => setTriageFilter(opt.v)}
              data-testid={`filter-triage-${opt.v || "todos"}`}
              className={`px-2 py-1 rounded-full border text-[11px] font-semibold transition-colors ${triageFilter === opt.v ? (opt.tone || "bg-navy text-white border-navy") : "bg-white text-navy border-tint hover:border-blue"}`}
            >
              {opt.l}
            </button>
          ))}
        </div>
        {(dpoFilter || triageFilter) && (
          <button
            type="button"
            onClick={() => { setDpoFilter(""); setTriageFilter(""); }}
            data-testid="clear-service-filters"
            className="text-muted2 hover:text-navy underline text-[11px]"
          >
            Limpar filtros
          </button>
        )}
        <div className="flex items-center gap-1.5 ml-auto">
          <button
            type="button"
            onClick={() => setArquivadasFilter((v) => !v)}
            data-testid="filter-arquivadas"
            className={`px-2 py-1 rounded-full border text-[11px] font-semibold transition-colors ${arquivadasFilter ? "bg-navy text-white border-navy" : "bg-white text-navy border-tint hover:border-navy"}`}
            title="Mostrar apenas leads arquivadas"
          >
            {arquivadasFilter ? "A mostrar arquivadas" : "Mostrar arquivadas"}
          </button>
          <Mail size={13} className="text-blue" />
          <span className="text-muted2 font-semibold uppercase tracking-wider">Newsletter:</span>
          <button
            type="button"
            onClick={() => setMarketingFilter((v) => !v)}
            data-testid="filter-marketing-optin"
            className={`px-2 py-1 rounded-full border text-[11px] font-semibold transition-colors ${marketingFilter ? "bg-blue text-white border-blue" : "bg-white text-navy border-tint hover:border-blue"}`}
          >
            {marketingFilter ? "So opt-in marketing" : "Todos"}
          </button>
          {marketingFilter && (
            <button
              type="button"
              onClick={exportContactsCsv}
              data-testid="export-marketing-csv"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-tint bg-white text-navy hover:border-blue text-[11px] font-semibold"
              title="Exportar contactos marketing opt-in em CSV (RGPD)"
            >
              <Download size={11} /> CSV
            </button>
          )}
        </div>
      </div>

      {arquivadasFilter && rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="archived-summary">
          <span className="text-[11px] text-muted2 font-semibold uppercase tracking-wider">Arquivadas por motivo:</span>
          {Object.entries(rows.reduce((acc, r) => {
            const label = (r.arquivo_motivo || "Sem motivo").split(" - ")[0];
            acc[label] = (acc[label] || 0) + 1;
            return acc;
          }, {})).map(([motivo, n]) => (
            <span key={motivo} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-tint text-[11px]">
              {motivo}: <b>{n}</b>
            </span>
          ))}
        </div>
      )}

      {/* Filtros de Lead Score (Iter98c) - aplicam-se apenas a leads (contas ficam sempre visiveis) */}
      <div className="flex flex-wrap items-center gap-2 mb-4 text-xs" data-testid="accounts-score-filters">
        <span className="text-muted2 font-semibold uppercase tracking-wider">Lead score:</span>
        <button type="button" onClick={() => setSortByScore((v) => !v)}
          data-testid="filter-sort-score"
          className={`px-2 py-1 rounded-full border text-[11px] font-semibold transition-colors ${sortByScore ? "bg-blue text-white border-blue" : "bg-white text-navy border-tint hover:border-blue"}`}>
          {sortByScore ? "Ordenado por score DESC" : "Ordenar por score"}
        </button>
        {[
          { v: 0,  l: "Todos" },
          { v: 40, l: "40+" },
          { v: 60, l: "60+" },
          { v: 80, l: "80+ HOT" },
        ].map((o) => (
          <button key={o.v} type="button" onClick={() => setMinScore(o.v)}
            data-testid={`filter-min-score-${o.v}`}
            className={`px-2 py-1 rounded-full border text-[11px] font-semibold transition-colors ${minScore === o.v ? "bg-navy text-white border-navy" : "bg-white text-navy border-tint hover:border-navy"}`}>
            {o.l}
          </button>
        ))}
        <span className="text-muted2 font-semibold uppercase tracking-wider ml-2">Classificacao:</span>
        <button type="button" onClick={() => setOportunidadeFilter((v) => !v)}
          data-testid="filter-oportunidades"
          className={`px-2 py-1 rounded-full border text-[11px] font-semibold transition-colors ${oportunidadeFilter ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-navy border-tint hover:border-emerald-500"}`}>
          {oportunidadeFilter ? "So oportunidades" : "Todas"}
        </button>
        <span className="text-muted2 font-semibold uppercase tracking-wider ml-2">Ordenar:</span>
        <select value={sortMode} onChange={(e) => setSortMode(e.target.value)} data-testid="accounts-sort-mode"
          disabled={sortByScore} title={sortByScore ? "Desative 'Ordenar por score' para escolher outra ordenacao" : ""}
          className="px-2 py-1 rounded-full border border-tint bg-white text-[11px] font-semibold text-navy disabled:opacity-50">
          <option value="alpha">Alfabetica (A-Z)</option>
          <option value="recent">Mais recentes</option>
        </select>
      </div>


      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="accounts-cards">
        {displayRows.length === 0 && (
          <div className="col-span-full text-center text-muted2 border border-tint rounded-lg py-12">
            {plataforma ? `Sem contas ligadas a subscricoes de ${plataforma}.` : "Sem contas. Crie uma conta ou um lead."}
          </div>
        )}
        {pagedRows.map((r) => {
          const score = r.lead_score || 0;
          const scoreTone = score >= 80 ? "bg-red-100 text-red-700 border border-red-200"
            : score >= 60 ? "bg-amber-100 text-amber-700 border border-amber-200"
            : score >= 40 ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
            : "bg-sky-100 text-sky-700 border border-sky-200";
          const scoreLabel = score >= 80 ? "HOT" : score >= 60 ? "WARM" : score >= 40 ? "SQL" : "MQL";
          return (
          <button
            key={r.id}
            onClick={() => setDrawerId(r.id)}
            data-testid={`account-card-${r.id}`}
            className="text-left border border-tint rounded-lg p-4 hover:border-blue hover:shadow-sm transition-all bg-white"
          >
            <div className="flex items-start justify-between mb-1 gap-2">
              <span className="font-semibold text-navy flex items-center gap-2 min-w-0">
                {r.credito_estado && (
                  <span
                    data-testid={`credit-dot-${r.id}`}
                    title={r.credito_estado === "vermelho" ? `Limite de credito excedido (${eur(r.divida_aberta)} em aberto)` : r.credito_estado === "ambar" ? `Divida em aberto: ${eur(r.divida_aberta)}` : "Sem divida em aberto"}
                    className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${r.credito_estado === "vermelho" ? "bg-red-500" : r.credito_estado === "ambar" ? "bg-amber-500" : "bg-emerald-500"}`}
                  />
                )}
                <span className="truncate"><HighlightText text={r.nome} query={q} /></span>
              </span>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className="flex items-center gap-1.5">
                  {isSuper && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => openEditAccount(e, r.id)}
                      data-testid={`account-edit-${r.id}`}
                      title="Editar conta/lead (super admin)"
                      className="text-muted2 hover:text-blue hover:bg-blue/10 rounded p-1 cursor-pointer"
                    >
                      <Pencil size={13} />
                    </span>
                  )}
                  <Badge value={r.tipo} />
                </span>
                {r.tipo === "lead" && (
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${scoreTone}`}
                    data-testid={`account-score-${r.id}`}
                    title={`Ciclo sugerido: ${scoreLabel}`}
                  >
                    {scoreLabel} {score}
                  </span>
                )}
                {r.tipo === "lead" && (r.ciclo_vida === "opportunity" || r.n_oportunidades > 0) && (
                  <span className="flex items-center gap-1" data-testid={`account-classificacao-${r.id}`}>
                    {r.ciclo_vida === "opportunity" && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">
                        Oportunidade
                      </span>
                    )}
                    {r.n_oportunidades > 0 && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 border border-indigo-200">
                        No Funil
                      </span>
                    )}
                  </span>
                )}
                <span
                  role="button" tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); nav(`/app/crm/contas/${r.id}`); }}
                  data-testid={`account-open-${r.id}`}
                  className="text-[11px] font-semibold text-blue hover:underline cursor-pointer whitespace-nowrap"
                  title="Abrir ficha completa"
                >
                  Abrir ficha completa
                </span>
              </div>
            </div>
            {r.codigo && <div className="text-[10px] font-mono text-blue mb-1">{r.codigo}</div>}
            <div className="text-xs text-muted2">{r.nif ? `NIF ${r.nif}` : "Sem NIF"} - {r.setor || "Setor n/d"}</div>
            {(r.dpo_mode || r.triage_mode) && (
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-testid={`account-service-badges-${r.id}`}>
                {r.dpo_mode && (
                  <span
                    title={r.dpo_mode === "cogniseg" ? "DPO prestado pela Cogniseg (Privaxia)" : "DPO gerido pelo cliente (Privaxia)"}
                    data-testid={`badge-dpo-${r.id}`}
                    className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${r.dpo_mode === "cogniseg" ? "bg-blue/10 text-blue" : "bg-amber-100 text-amber-700"}`}
                  >
                    <Shield size={10} /> DPO {r.dpo_mode === "cogniseg" ? "Cogniseg" : "Cliente"}
                  </span>
                )}
                {r.triage_mode && (
                  <span
                    title={r.triage_mode === "cogniseg" ? "Triagem prestada pela Cogniseg (CogniEtica)" : "Triagem gerida pelo cliente (CogniEtica)"}
                    data-testid={`badge-triage-${r.id}`}
                    className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${r.triage_mode === "cogniseg" ? "bg-blue/10 text-blue" : "bg-amber-100 text-amber-700"}`}
                  >
                    <ClipboardCheck size={10} /> Triagem {r.triage_mode === "cogniseg" ? "Cogniseg" : "Cliente"}
                  </span>
                )}
              </div>
            )}
            <div className="text-xs text-muted2 mt-2">{r.n_contactos} contactos - {r.n_oportunidades} oportunidades{r.divida_aberta > 0 ? <span className={r.credito_estado === "vermelho" ? "text-red-600 font-semibold" : "text-amber-600 font-semibold"}> - {eur(r.divida_aberta)} em aberto</span> : ""}</div>
          </button>
          );
        })}
      </div>
      {displayRows.length > PAGE_SIZE_OPTIONS[0] && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-1 py-3 text-xs text-muted2" data-testid="accounts-pagination">
          <span>A mostrar <b className="text-navy">{pageFrom}-{pageTo}</b> de <b className="text-navy">{displayRows.length}</b> contas</span>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1">
              Por pagina:
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                data-testid="accounts-page-size"
                className="px-2 py-1 border border-tint rounded bg-white text-navy font-semibold">
                {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
              data-testid="accounts-prev-page"
              className="px-2 py-1 border border-tint rounded bg-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50">Anterior</button>
            <span className="font-semibold text-navy">Pagina {page} / {totalPages}</span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              data-testid="accounts-next-page"
              className="px-2 py-1 border border-tint rounded bg-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50">Seguinte</button>
          </div>
        </div>
      )}

      <Modal open={showAccount} onClose={() => { setShowAccount(false); setEditingId(null); }} title={editingId ? "Editar conta / lead" : "Nova conta"} testid="account-modal" size="xl">
        {/* Codigo + Tipo + Origem */}
        <div className="flex items-center gap-3 bg-blue/5 border border-blue/20 rounded-md px-3 py-2">
          <div className="text-xs">
            <div className="text-muted2 uppercase tracking-wider">Codigo</div>
            <div className="font-mono font-bold text-navy" data-testid="account-codigo">{nextCode || "-"}</div>
          </div>
          <button type="button" onClick={() => refreshCode(acc.tipo)} className="text-muted2 hover:text-navy" title="Atualizar codigo"><RefreshCw size={14} /></button>
          <div className="flex-1" />
          <div className="text-xs text-muted2">Atribuido ao criar</div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Selecter label="Tipo" value={acc.tipo} onChange={(v) => { setField({ tipo: v }); refreshCode(v); }} options={TIPOS} testid="account-tipo" />
          <Selecter label="Origem" value={acc.origem} onChange={(v) => setField({ origem: v, ...(v !== "parceiro" ? { parceiro_id: "", parceiro_nome: "" } : {}) })} options={ORIGENS} testid="account-origem" />
        </div>
        {acc.origem === "parceiro" && (
          <div className="border border-blue/20 bg-blue/5 rounded-lg p-3 flex items-center gap-3">
            <Handshake size={16} className="text-blue flex-shrink-0" />
            <div className="flex-1">
              <label className="text-xs font-semibold text-navy block mb-1">Parceiro de captura</label>
              <select value={acc.parceiro_id || ""} onChange={(e) => {
                const p = partners.find((x) => x.id === e.target.value);
                setField({ parceiro_id: e.target.value, parceiro_nome: p?.nome || "" });
              }} data-testid="account-parceiro-select" className="w-full px-2 py-1.5 border border-tint rounded text-sm bg-white focus:border-blue focus:outline-none">
                <option value="">- Selecionar parceiro -</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            </div>
            {acc.parceiro_nome && <span className="text-xs text-blue font-semibold bg-blue/10 px-2 py-1 rounded">{acc.parceiro_nome}</span>}
          </div>
        )}

        <SectionTitle>Identificacao</SectionTitle>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Field label="Nome" value={acc.nome} onChange={(v) => setField({ nome: v })} placeholder="Nome da organizacao" testid="account-nome" />
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Field label="NIF" value={acc.nif} onChange={(v) => setField({ nif: v })} placeholder="Numero de contribuinte" testid="account-nif" />
            </div>
            <button
              type="button"
              onClick={doVies}
              disabled={busy.vies || !acc.nif.trim()}
              data-testid="account-vies-btn"
              className="mb-0.5 inline-flex items-center gap-1 px-2.5 py-2 text-xs font-semibold bg-blue text-white rounded-md hover:bg-[var(--blue-hover)] disabled:opacity-50"
              title="Consultar VIES"
            >
              <Building2 size={13} /> {busy.vies ? "..." : "VIES"}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="col-span-2"><Field label="Website" value={acc.website} onChange={(v) => setField({ website: v })} placeholder="https://" testid="account-website" /></div>
          <Field label="Email geral" value={acc.email} onChange={(v) => setField({ email: v })} testid="account-email" />
          <Field label="Telefone" value={acc.telefone} onChange={(v) => setField({ telefone: v })} testid="account-telefone" />
        </div>

        <SectionTitle>Morada</SectionTitle>
        <div className="grid grid-cols-4 gap-3">
          <div className="col-span-2"><Field label="Morada" value={acc.morada} onChange={(v) => setField({ morada: v })} testid="account-morada" /></div>
          <Field label="Codigo Postal" value={acc.codigo_postal} onChange={(v) => {
              setField({ codigo_postal: v });
              // auto-lookup quando o CP fica completo
              if (v && /^\d{4}-\d{3}$/.test(v)) doCPLookup(v, "principal");
            }}
            mask={masks.cp_pt} placeholder="4760-656" maxLength={8}
            pattern="\d{4}-\d{3}" title="Formato: NNNN-NNN"
            testid="account-cp" />
          <Field label="Localidade" value={acc.localidade} onChange={(v) => setField({ localidade: v })} testid="account-localidade" />
          <Field label="Cidade" value={acc.cidade} onChange={(v) => setField({ cidade: v })} testid="account-cidade" />
          <Field label="Distrito" value={acc.distrito} onChange={(v) => setField({ distrito: v })} testid="account-distrito" placeholder="Preenchido pelo CP/VIES" />
          <div className="col-span-1"><Field label="Pais" value={acc.pais} onChange={(v) => setField({ pais: v })} testid="account-pais" /></div>
        </div>

        <SectionTitle>Classificacao</SectionTitle>
        <div className="flex justify-end items-center gap-2 -mt-2 mb-1">
          <button type="button" onClick={doCAE} disabled={busy.cae || (!acc.nif.trim() && !acc.nome.trim())} data-testid="account-cae-btn"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50">
            <Search size={13} /> {busy.cae ? "A pesquisar..." : "Pesquisar CAE"}
          </button>
          <button type="button" onClick={doAI} disabled={busy.ai} data-testid="account-ai-btn"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-navy text-white rounded-md hover:bg-navy/90 disabled:opacity-50">
            <Sparkles size={13} /> {busy.ai ? "A enriquecer..." : "Enriquecer com IA"}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Setor" value={acc.setor} onChange={(v) => setField({ setor: v })} testid="account-setor" />
          <Field label="Area de negocio" value={acc.area_negocio} onChange={(v) => setField({ area_negocio: v })} testid="account-area" />
          <Selecter label="Dimensao" value={acc.dimensao} onChange={(v) => setField({ dimensao: v })} options={DIMENSOES} testid="account-dimensao" />
        </div>
        <div className="grid grid-cols-3 gap-3 mt-2">
          <Field label="CAE" value={acc.cae} onChange={(v) => setField({ cae: v })} testid="account-cae" placeholder="Ex: 62010" />
        </div>

        <SectionTitle>Dados de faturacao</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Faturar a nome de" value={acc.fatura_a_nome_de} onChange={(v) => setField({ fatura_a_nome_de: v })} testid="account-fatura-nome" />
          <Field label="Email de faturacao" value={acc.contacto_faturacao_email} onChange={(v) => setField({ contacto_faturacao_email: v })} testid="account-fatura-email" />
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="col-span-2"><Field label="Morada faturacao" value={acc.morada_faturacao} onChange={(v) => setField({ morada_faturacao: v })} testid="account-fatura-morada" /></div>
          <Field label="CP faturacao" value={acc.codigo_postal_faturacao} onChange={(v) => {
              setField({ codigo_postal_faturacao: v });
              if (v && /^\d{4}-\d{3}$/.test(v)) doCPLookup(v, "faturacao");
            }}
            mask={masks.cp_pt} placeholder="4760-656" maxLength={8}
            pattern="\d{4}-\d{3}" title="Formato: NNNN-NNN"
            testid="account-fatura-cp" />
          <Field label="Localidade faturacao" value={acc.localidade_faturacao} onChange={(v) => setField({ localidade_faturacao: v })} testid="account-fatura-localidade" />
          <Field label="Cidade faturacao" value={acc.cidade_faturacao} onChange={(v) => setField({ cidade_faturacao: v })} testid="account-fatura-cidade" />
          <Field label="Distrito faturacao" value={acc.distrito_faturacao} onChange={(v) => setField({ distrito_faturacao: v })} testid="account-fatura-distrito" placeholder="Preenchido pelo CP/VIES" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="IBAN" value={acc.iban} onChange={(v) => setField({ iban: v })} testid="account-iban" />
          <Field label="Condicoes de pagamento" value={acc.condicoes_pagamento} onChange={(v) => setField({ condicoes_pagamento: v })} testid="account-cond-pag" />
          <Selecter label="Metodo preferido" value={acc.metodo_pagamento_pref} onChange={(v) => setField({ metodo_pagamento_pref: v })} options={[{ value: "", label: "- Nao definido -" }, "sepa", "mb_way", "transferencia", "cartao", "multibanco"]} testid="account-metodo" />
        </div>

        <SectionTitle>RGPD</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <Selecter label="Base legal (art. 6 RGPD)" value={acc.rgpd_base_legal} onChange={(v) => setField({ rgpd_base_legal: v })} options={RGPD_BASE_LEGAL} testid="account-rgpd-base" />
          <Selecter label="Canal de captura" value={acc.rgpd_canal_captura} onChange={(v) => setField({ rgpd_canal_captura: v, ...(v !== "parceiro" ? { parceiro_id: acc.parceiro_id, parceiro_nome: acc.parceiro_nome } : {}) })} options={[{ value: "", label: "- Nao definido -" }, ...CANAL_CAPTURA.map((c) => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }))]} testid="account-rgpd-canal" />
        </div>
        {acc.rgpd_canal_captura === "parceiro" && (
          <div className="border border-blue/20 bg-blue/5 rounded-lg p-3 flex items-center gap-3">
            <Handshake size={16} className="text-blue flex-shrink-0" />
            <div className="flex-1">
              <label className="text-xs font-semibold text-navy block mb-1">Parceiro (canal RGPD)</label>
              <select value={acc.parceiro_id || ""} onChange={(e) => {
                const p = partners.find((x) => x.id === e.target.value);
                setField({ parceiro_id: e.target.value, parceiro_nome: p?.nome || "" });
              }} data-testid="account-parceiro-rgpd-select" className="w-full px-2 py-1.5 border border-tint rounded text-sm bg-white focus:border-blue focus:outline-none">
                <option value="">- Selecionar parceiro -</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            </div>
          </div>
        )}
        <TextArea label="Finalidade do tratamento" value={acc.rgpd_finalidade} onChange={(v) => setField({ rgpd_finalidade: v })} testid="account-rgpd-finalidade" />
        <div className="grid grid-cols-3 gap-3">
          <Field label="Data do consentimento" type="date" value={acc.rgpd_consentimento_em} onChange={(v) => setField({ rgpd_consentimento_em: v })} testid="account-rgpd-data" />
          <Field label="Retencao (meses)" type="number" value={acc.rgpd_retencao_meses} onChange={(v) => setField({ rgpd_retencao_meses: v })} testid="account-rgpd-retencao" />
          <div className="flex items-end pb-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!acc.rgpd_direito_esquecimento} onChange={(e) => setField({ rgpd_direito_esquecimento: e.target.checked })} data-testid="account-rgpd-erasure" />
              Direito ao esquecimento
            </label>
          </div>
        </div>

        <SectionTitle>Notas</SectionTitle>
        <TextArea label="Notas internas" value={acc.notas} onChange={(v) => setField({ notas: v })} testid="account-notas" />

        <div className="flex justify-end gap-2 pt-3 sticky bottom-0 bg-white -mx-6 px-6 py-3 border-t border-tint">
          <Btn variant="ghost" onClick={() => { setShowAccount(false); setEditingId(null); }}>Cancelar</Btn>
          <Btn onClick={createAccount} testid="account-save">{busy.save ? "A guardar..." : (editingId ? "Guardar alteracoes" : "Criar conta")}</Btn>
        </div>
      </Modal>

      <Modal open={showLead} onClose={() => setShowLead(false)} title="Novo lead" testid="lead-modal">
        <p className="text-xs text-muted2">Regista o interesse de um potencial cliente. Cria a conta e o contacto, classificados como LEAD (nao cria oportunidade automaticamente).</p>
        <Field label="Nome do contacto" value={lead.nome_contacto} onChange={(v) => setLead({ ...lead, nome_contacto: v })} testid="lead-nome" />
        <Field label="Email" value={lead.email} onChange={(v) => setLead({ ...lead, email: v })} testid="lead-email" />
        <Field label="Telefone" value={lead.telefone} onChange={(v) => setLead({ ...lead, telefone: v })} testid="lead-telefone" />
        <Field label="Organizacao" value={lead.organizacao} onChange={(v) => setLead({ ...lead, organizacao: v })} testid="lead-org" />
        <Selecter label="Origem" value={lead.origem} onChange={(v) => setLead({ ...lead, origem: v })} options={ORIGENS} testid="lead-origem" />
        <Selecter label="Plataforma de interesse"
          value={lead.plataforma}
          onChange={(v) => setLead({ ...lead, plataforma: v })}
          options={[{ value: "", label: "- Sem plataforma especifica -" }, ...platforms.map((p) => ({ value: p.slug || p.nome, label: p.nome || p.slug }))]}
          testid="lead-plataforma" />
        <TextArea label="Mensagem" value={lead.mensagem} onChange={(v) => setLead({ ...lead, mensagem: v })} testid="lead-mensagem" />
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="ghost" onClick={() => setShowLead(false)}>Cancelar</Btn>
          <Btn onClick={createLead} testid="lead-save">Criar lead</Btn>
        </div>
      </Modal>

      <AccountDrawer accountId={drawerId} open={!!drawerId} onClose={() => setDrawerId(null)} />
    </>
  );
}
